import { Flex, theme, Typography } from 'antd';
import type { GlobalToken } from 'antd';
import { useTranslation } from 'react-i18next';
import { CheckIcon, PlayIcon } from '@phosphor-icons/react';
import styled from 'styled-components';

import { Modal } from '../Modal';
import { resolveBackground } from './gradients';
import type { PlaybookDetails } from './types';
import { radius } from '../../theme/radius';

export type { PlaybookGradient } from './gradients';
export type { PlaybookAddOnType, PlaybookDetails } from './types';

export type PlaybookDetailsModalProps = {
  open: boolean;
  /** The playbook to describe; `null` renders nothing (modal stays closed). */
  playbook: PlaybookDetails | null;
  /** Owner/category eyebrow shown above the name (e.g. "by Acme University"). */
  owner?: string;
  /** Whether this playbook is the currently-selected one. */
  selected: boolean;
  /** Select this playbook (the modal closes itself afterwards). */
  onSelect: () => void;
  onClose: () => void;
};

/**
 * Read-only playbook details modal for the experience-creation flow. Shows the
 * playbook's artwork, name and description, plus a primary action to select it.
 * Built on the shared `@libertify/ui` Modal.
 */
export const PlaybookDetailsModal = ({
  open,
  playbook,
  owner,
  selected,
  onSelect,
  onClose,
}: PlaybookDetailsModalProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false });
  const { token } = theme.useToken();

  if (!playbook) return null;

  const { name, description, gradient, addOns } = playbook;

  const handleSelect = () => {
    onSelect();
    onClose();
  };

  return (
    <Modal
      open={open}
      size="large"
      showFooter={false}
      onClose={onClose}
      styles={{ body: { padding: 0 } }}
    >
      <Split>
        <Art $bg={resolveBackground(gradient)} align="center" justify="center">
          <MockScreen $token={token} vertical gap={10}>
            <MockVideo $token={token} align="center" justify="center">
              <MockPlay $token={token} align="center" justify="center">
                <PlayIcon size={18} weight="fill" />
              </MockPlay>
            </MockVideo>
            <MockBar $token={token} $w="80%" />
            <MockBar $token={token} $w="60%" />
            <MockBar $token={token} $w="45%" />
          </MockScreen>
        </Art>

        <Flex vertical flex="1 1 45%" style={{ padding: token.paddingLG }}>
          {owner && (
            <Typography.Text
              style={{
                display: 'block',
                fontSize: token.fontSizeSM,
                color: token.colorTextTertiary,
                marginBottom: 4,
              }}
            >
              {owner}
            </Typography.Text>
          )}
          <Typography.Title
            level={3}
            style={{
              margin: '0 0 8px',
              letterSpacing: '-0.03em',
              lineHeight: 1.2,
            }}
          >
            {name}
          </Typography.Title>
          {description && (
            <Typography.Paragraph
              type="secondary"
              style={{ lineHeight: 1.5, marginBottom: 8 }}
            >
              {description}
            </Typography.Paragraph>
          )}

          {addOns && addOns.length > 0 ? (
            <>
              <Typography.Text
                strong
                style={{
                  display: 'block',
                  fontSize: token.fontSizeSM,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: token.colorTextTertiary,
                  margin: '20px 0 12px',
                }}
              >
                {t('components.playbook.details.addOnsTitle')}
              </Typography.Text>
              <AddOnGrid>
                {addOns.map((addOn) => (
                  <AddOnItem key={addOn} align="center" gap={10}>
                    <Dot />
                    <Typography.Text
                      style={{ fontSize: 13, fontWeight: 600, color: '#090e19' }}
                    >
                      {t(`components.playbook.details.addOns.${addOn}`)}
                    </Typography.Text>
                  </AddOnItem>
                ))}
              </AddOnGrid>
            </>
          ) : null}

          <SelectButton
            $token={token}
            $selected={selected}
            type="button"
            onClick={handleSelect}
          >
            {selected && <CheckIcon size={18} weight="bold" />}
            {selected
              ? t('components.playbook.details.selectedCta')
              : t('components.playbook.details.selectCta')}
          </SelectButton>
        </Flex>
      </Split>
    </Modal>
  );
};

export default PlaybookDetailsModal;

// ─── Styled components ───

interface TokenProps {
  $token: GlobalToken;
}

const Split = styled(Flex)`
  min-height: 460px;

  @media (width <= 720px) {
    flex-direction: column;
    min-height: 0;
  }
`;

const Art = styled(Flex)<{ $bg: string }>`
  flex: 1 1 55%;
  padding: 32px;
  background: ${({ $bg }) => $bg};
  background-size: cover;
  background-position: center;

  @media (width <= 720px) {
    flex-basis: auto;
    min-height: 220px;
  }
`;

const MockScreen = styled(Flex)<TokenProps>`
  width: 100%;
  max-width: 360px;
  padding: 16px;
  border-radius: ${radius.box}px;
  background: ${({ $token }) => $token.colorBgContainer};
  box-shadow: ${({ $token }) => $token.boxShadow};
`;

const MockVideo = styled(Flex)<TokenProps>`
  aspect-ratio: 16 / 9;
  border-radius: ${radius.box}px;
  background: ${({ $token }) => $token.colorFillSecondary};
`;

const MockPlay = styled(Flex)<TokenProps>`
  width: 44px;
  height: 44px;
  border-radius: ${radius.circle};
  color: ${({ $token }) => $token.colorWhite};
  background: ${({ $token }) => $token.colorPrimary};
`;

const MockBar = styled.div<TokenProps & { $w: string }>`
  height: 8px;
  width: ${({ $w }) => $w};
  border-radius: ${radius.pill}px;
  background: ${({ $token }) => $token.colorFillSecondary};
`;

const AddOnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 8px;

  @media (width <= 720px) {
    grid-template-columns: 1fr;
  }
`;

// Colors are pinned to the design spec (light-theme prototype), not theme tokens.
const AddOnItem = styled(Flex)`
  padding: 8px 10px;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
`;

const Dot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: ${radius.circle};
  background: #3b82f6;
`;

const SelectButton = styled.button<TokenProps & { $selected: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 48px;
  margin-top: auto;
  padding: 0 20px;
  border-radius: ${radius.pill}px;
  font-family: inherit;
  font-size: ${({ $token }) => $token.fontSize}px;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ $token }) => $token.motionDurationFast};
  border: 1.5px solid ${({ $token }) => $token.colorPrimary};
  background: ${({ $token, $selected }) =>
    $selected ? $token.colorPrimaryBg : $token.colorPrimary};
  color: ${({ $token, $selected }) =>
    $selected ? $token.colorPrimary : $token.colorWhite};

  &:hover {
    background: ${({ $token, $selected }) =>
      $selected ? $token.colorPrimaryBgHover : $token.colorPrimaryHover};
    border-color: ${({ $token }) => $token.colorPrimaryHover};
  }

  &:focus-visible {
    outline: 2px solid ${({ $token }) => $token.colorPrimary};
    outline-offset: 2px;
  }
`;
